import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import './Purchases.css';

const apiBaseURL = (process.env.REACT_APP_API_BASE_URL || 'https://api.vehiclesolution.co').replace(/\/+$/, '');

function IssuedPartsList({ onLogout }) {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  const fetchIssuedParts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseURL}/api/parts/issues?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(searchTerm.trim())}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load issued parts');
      }

      setIssues(data.issues || []);
      setPagination(data.pagination || { page, pageSize, total: 0, totalPages: 1 });
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load issued parts');
    } finally {
      setLoading(false);
    }
  }, [fromDate, page, pageSize, searchTerm, toDate]);

  useEffect(() => {
    fetchIssuedParts();
  }, [fetchIssuedParts]);

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  return (
    <div className="home-container app-shell-fixed">
      <Sidebar onLogout={onLogout} />
      <div className="main-section app-fixed-main">
        <div className="purchases-container app-scroll-layout">
          <header className="purchases-header">
            <button className="app-back-button" onClick={() => navigate('/inventory')}>
              Back
            </button>
            <div>
              <h1>Issued Parts</h1>
              <p>View all parts issued from inventory, including job card deliveries.</p>
            </div>
            <button className="btn-refresh" onClick={fetchIssuedParts} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </header>

          <div className="purchases-filters">
            <input
              type="text"
              className="purchases-search"
              placeholder="Search part no, name, reference, issued to, reason..."
              value={searchTerm}
              onChange={(event) => {
                setPage(1);
                setSearchTerm(event.target.value);
              }}
            />
            <div className="date-filter">
              <label htmlFor="fromDate">From Date</label>
              <input
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(event) => {
                  setPage(1);
                  setFromDate(event.target.value);
                }}
              />
            </div>
            <div className="date-filter">
              <label htmlFor="toDate">To Date</label>
              <input
                id="toDate"
                type="date"
                value={toDate}
                onChange={(event) => {
                  setPage(1);
                  setToDate(event.target.value);
                }}
              />
            </div>
            <div className="date-filter">
              <label htmlFor="pageSize">Rows</label>
              <select
                id="pageSize"
                className="purchases-page-size"
                value={pageSize}
                onChange={(event) => {
                  setPage(1);
                  setPageSize(parseInt(event.target.value, 10));
                }}
              >
                <option value={10}>10 rows</option>
                <option value={20}>20 rows</option>
                <option value={50}>50 rows</option>
              </select>
            </div>
          </div>

          {error && <div className="purchases-error">{error}</div>}

          <div className="app-scroll-results">
            <div className="purchases-table-wrapper app-grid-scroll">
              {loading ? (
                <div className="purchases-empty">Loading issued parts...</div>
              ) : issues.length === 0 ? (
                <div className="purchases-empty">No issued parts found for the current filters.</div>
              ) : (
                <table className="purchases-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Issue Date</th>
                      <th>Part ID</th>
                      <th>Part No</th>
                      <th>Part Name</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Reference</th>
                      <th>Issued To</th>
                      <th>Reason</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((issue) => (
                      <tr key={issue.IssuePartId}>
                        <td>{issue.IssuePartId}</td>
                        <td>{formatDate(issue.IssueDate)}</td>
                        <td>{issue.PartId ?? '-'}</td>
                        <td>{issue.PartNo || '-'}</td>
                        <td>{issue.PartName || '-'}</td>
                        <td>{issue.Quantity ?? 0}</td>
                        <td>{issue.Unit || '-'}</td>
                        <td>{issue.ReferenceNo || '-'}</td>
                        <td>{issue.IssuedTo || '-'}</td>
                        <td>{issue.Reason || '-'}</td>
                        <td>{issue.CreatedByEmail || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {!loading && !error && (
              <div className="purchases-pagination">
                <div className="purchases-page-info">
                  Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
                </div>
                <div className="purchases-page-buttons">
                  <button
                    className="btn-refresh"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-refresh"
                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default IssuedPartsList;
